/**
 * QrCodeCard — the "QR Code Ready" feature the platform homepage promises.
 *
 * Shows a live QR code for the restaurant's public menu (and each active
 * branch), lets the admin download a high-resolution PNG for print, and opens
 * a print-ready table card (QR + restaurant name + scan hint) in one click.
 *
 * QR modules are always near-black on white — maximum scan reliability — while
 * the surrounding card carries the restaurant's brand color.
 */
import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { useLanguage } from '../context/LanguageContext';

const QR_OPTS = { errorCorrectionLevel: 'M', margin: 2, color: { dark: '#111111', light: '#ffffff' } };

export default function QrCodeCard({ restaurant }) {
  const { ta } = useLanguage();
  const [branchSlug, setBranchSlug] = useState('');     // '' = main menu
  const [dataUrl, setDataUrl] = useState('');
  const canvasRef = useRef(null);

  const branches = (restaurant.branches || []).filter(b => b.isActive);
  const menuUrl = `${window.location.origin}/menu${branchSlug ? `?branch=${branchSlug}` : ''}`;

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(menuUrl, { ...QR_OPTS, width: 280 })
      .then(url => { if (alive) setDataUrl(url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [menuUrl]);

  const markQrDone = () => { try { localStorage.setItem(`nooshin_qr_done_${restaurant.slug}`, '1'); } catch {} };

  const download = async () => {
    markQrDone();
    const url = await QRCode.toDataURL(menuUrl, { ...QR_OPTS, width: 1024 });
    const a = document.createElement('a');
    a.href = url;
    a.download = `${restaurant.slug}${branchSlug ? '-' + branchSlug : ''}-menu-qr.png`;
    a.click();
  };

  const printCard = async () => {
    markQrDone();
    const url = await QRCode.toDataURL(menuUrl, { ...QR_OPTS, width: 600 });
    const branchName = branchSlug ? (branches.find(b => b.slug === branchSlug)?.name || '') : '';
    const w = window.open('', '_blank', 'width=480,height=680');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>${restaurant.name} — QR</title>
      <style>
        body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:Georgia,serif;background:#fff}
        .card{text-align:center;padding:40px;border:3px solid ${restaurant.primaryColor || '#c8a96e'};border-radius:24px;margin:24px}
        h1{font-size:28px;margin:0 0 4px;color:#111}
        .branch{font-size:15px;color:#888;margin:0 0 18px}
        img{width:300px;height:300px}
        p{font-size:16px;color:#444;letter-spacing:.04em;margin:14px 0 0;text-transform:uppercase}
        @media print { .card{border-width:3px} }
      </style></head><body>
      <div class="card">
        <h1>${restaurant.name}</h1>
        ${branchName ? `<p class="branch">${branchName}</p>` : ''}
        <img src="${url}" alt="QR" />
        <p>${ta('adm_qr_scan_hint')}</p>
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="adm-qr-card">
      <div className="adm-qr-head">
        <span className="adm-qr-title">📲 {ta('adm_qr_title')}</span>
        <p className="adm-qr-sub">{ta('adm_qr_sub')}</p>
      </div>

      <div className="adm-qr-body">
        <div className="adm-qr-img-wrap" ref={canvasRef}>
          {dataUrl && <img src={dataUrl} alt="Menu QR code" className="adm-qr-img" />}
        </div>

        <div className="adm-qr-side">
          {branches.length > 0 && (
            <div className="adm-qr-branches">
              <button className={`adm-qr-branch-btn ${!branchSlug ? 'active' : ''}`}
                onClick={() => setBranchSlug('')}>🏠 {ta('adm_qr_main')}</button>
              {branches.map(b => (
                <button key={b.slug} className={`adm-qr-branch-btn ${branchSlug === b.slug ? 'active' : ''}`}
                  onClick={() => setBranchSlug(b.slug)}>{b.name}</button>
              ))}
            </div>
          )}
          <div className="adm-qr-url">{menuUrl}</div>
          <div className="adm-qr-actions">
            <button className="adm-qr-btn primary" onClick={download}>⬇️ {ta('adm_qr_download')}</button>
            <button className="adm-qr-btn" onClick={printCard}>🖨️ {ta('adm_qr_print')}</button>
          </div>
        </div>
      </div>

      <style>{`
        .adm-qr-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:16px;padding:1.5rem;margin-bottom:1.5rem}
        .adm-qr-title{font-size:.95rem;font-weight:700;color:#fff}
        .adm-qr-sub{font-size:.78rem;color:rgba(255,255,255,.4);margin:.35rem 0 0;line-height:1.55}
        .adm-qr-body{display:flex;gap:1.5rem;align-items:center;margin-top:1.1rem;flex-wrap:wrap}
        .adm-qr-img-wrap{width:150px;height:150px;border-radius:14px;overflow:hidden;background:#fff;padding:6px;flex-shrink:0;box-shadow:0 6px 24px rgba(0,0,0,.35)}
        .adm-qr-img{width:100%;height:100%;display:block}
        .adm-qr-side{flex:1;min-width:220px;display:flex;flex-direction:column;gap:.7rem}
        .adm-qr-branches{display:flex;flex-wrap:wrap;gap:.4rem}
        .adm-qr-branch-btn{padding:.4rem .85rem;border-radius:9px;border:1px solid rgba(255,255,255,.14);background:rgba(255,255,255,.05);color:rgba(255,255,255,.7);font-family:'DM Sans',sans-serif;font-size:.78rem;cursor:pointer;transition:all .2s}
        .adm-qr-branch-btn.active{background:var(--pri);border-color:var(--pri);color:#fff;font-weight:700}
        .adm-qr-url{font-family:monospace;font-size:.74rem;color:rgba(255,255,255,.45);background:rgba(0,0,0,.3);border:1px solid rgba(255,255,255,.08);border-radius:8px;padding:.5rem .7rem;overflow-wrap:anywhere}
        .adm-qr-actions{display:flex;gap:.6rem;flex-wrap:wrap}
        .adm-qr-btn{padding:.6rem 1.1rem;border-radius:10px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.06);color:#fff;font-family:'DM Sans',sans-serif;font-size:.82rem;font-weight:600;cursor:pointer;transition:all .2s}
        .adm-qr-btn:hover{background:rgba(255,255,255,.12)}
        .adm-qr-btn.primary{background:var(--pri);border-color:var(--pri)}
        .adm-qr-btn.primary:hover{opacity:.88}
      `}</style>
    </div>
  );
}
